import type { Channel, Member, Post, Limits, ServerMessage, ClientMessage, SignalData, MediaConnection } from "./protocol.ts";
import { displayOptions } from "./screen.ts";
import { createVoicePipeline, defaultAudio, loadAudio, normalizeAudio, saveAudio, silentAudio } from "./audio.ts";
import type { AudioFeedback, AudioPreferences, VoicePipeline } from "./audio.ts";

export type Feed = { id: string; peerId: string; channel: Channel; stream: MediaStream | null; status: RTCPeerConnectionState };
export type RoomState = { status: "connecting" | "online" | "offline" | "left"; self: Member | null; members: Member[]; posts: Post[]; feeds: Feed[]; waiting: string[]; voice: MediaStream | null; screen: MediaStream | null; audio: AudioFeedback; audioPreferences: AudioPreferences; error: string; limits: Limits; relayAvailable: boolean };
type Wire = { pc: RTCPeerConnection; peerId: string; channel: Channel; publishing: boolean; candidates: RTCIceCandidateInit[]; timeout?: ReturnType<typeof setTimeout> };

export class RoomClient {
  private state: RoomState = { status: "connecting", self: null, members: [], posts: [], feeds: [], waiting: [], voice: null, screen: null, audio: { ...silentAudio }, audioPreferences: { ...defaultAudio }, error: "", limits: { people: 50, publishers: 20, watching: 4 }, relayAvailable: false };
  private rtc: MediaConnection = { iceServers: [], iceTransportPolicy: "all" };
  private voicePipeline?: VoicePipeline;
  private voiceAbort?: AbortController;
  private listeners = new Set<() => void>();
  private socket: WebSocket | null = null;
  private wires = new Map<string, Wire>();
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  private generation = 0;
  private ended = false;
  private negotiations = new Map<string, Promise<void>>();
  private captureRevision: Record<Channel, number> = { voice: 0, screen: 0 };
  private authExpired: () => void;
  private roomKey?: string;
  constructor(authExpired: () => void, roomKey?: string) { this.authExpired = authExpired; this.roomKey = roomKey; this.state.audioPreferences = loadAudio(); }
  snapshot = () => this.state;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private update(patch: Partial<RoomState>) { this.state = { ...this.state, ...patch }; for (const listener of this.listeners) listener(); }
  dismissError = () => this.update({ error: "" });
  private send(message: ClientMessage) {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message)); return true;
  }
  connect = () => {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null; this.ended = false;
    this.update({ status: "connecting", error: "" });
    const address = new URL(this.roomKey ? `/room/${this.roomKey}` : "/room", window.location.href); address.protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(address);
    this.socket = socket;
    socket.onmessage = event => {
      if (this.socket !== socket) return;
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
        if (!message || typeof message.type !== "string") throw new Error("message");
      } catch { socket.close(1007, "Resposta inválida"); return; }
      const generation = this.generation;
      const run = async () => { if (this.socket === socket && this.generation === generation) await this.receive(message); };
      const report = () => { if (this.socket === socket && this.generation === generation) this.update({ error: "Uma transmissão não conectou. Use Tentar novamente para reconectar." }); };
      if (message.type === "demand" || message.type === "relay") {
        const id = message.connectionId;
        const task = (this.negotiations.get(id) ?? Promise.resolve()).then(run).catch(report);
        this.negotiations.set(id, task);
        void task.finally(() => { if (this.negotiations.get(id) === task) this.negotiations.delete(id); });
      } else void run().catch(report);
    };
    socket.onerror = () => socket.close();
    socket.onclose = event => {
      if (this.socket !== socket) return;
      this.socket = null; this.release();
      if (this.ended) return;
      const terminal = [4001, 4002, 4003, 4004].includes(event.code);
      this.update({ status: "offline", members: [], self: null, error: terminal ? event.reason : "A conexão caiu. Vamos tentar reconectar; os compartilhamentos foram encerrados." });
      if (event.code === 4003) { this.authExpired(); return; }
      if (!terminal) {
        void this.reconnectAfterCheckingSession(this.generation);
      }
    };
  };
  private async reconnectAfterCheckingSession(generation: number) {
    let expired = false;
    try {
      const response = await fetch(`/api/session${this.roomKey ? `?room=${encodeURIComponent(this.roomKey)}` : ""}`, { credentials: "same-origin", signal: AbortSignal.timeout(5000) });
      if (response.status === 401) expired = true;
      else if (response.ok) expired = (await response.json() as { user: unknown }).user === null;
    } catch { /* An unreachable server does not establish that the account expired. */ }
    if (this.ended || this.generation !== generation || this.socket) return;
    if (expired) { this.update({ error: "Sua sessão terminou. Entre novamente." }); this.authExpired(); return; }
    const delay = Math.min(1000 * 2 ** this.retryCount++, 10000) + Math.random() * 300;
    this.retryTimer = setTimeout(this.connect, delay);
  }
  leave = () => {
    this.ended = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const socket = this.socket; this.socket = null;
    socket?.close(); this.release();
    this.update({ status: "left", members: [], self: null, posts: [], error: "" });
  };
  private release() {
    this.generation++;
    this.captureRevision.voice++; this.captureRevision.screen++;
    this.negotiations.clear();
    for (const wire of this.wires.values()) { if (wire.timeout) clearTimeout(wire.timeout); wire.pc.close(); }
    this.wires.clear();
    this.voiceAbort?.abort(); this.voiceAbort = undefined;
    this.voicePipeline?.close(); this.voicePipeline = undefined;
    for (const stream of [this.state.voice, this.state.screen]) stream?.getTracks().forEach(track => track.stop());
    this.update({ feeds: [], waiting: [], voice: null, screen: null, audio: { ...silentAudio } });
  }
  async publish(channel: Channel, supplied?: MediaStream) {
    if (this.state.status !== "online") throw new Error("Entre na sala antes de transmitir.");
    if (this.state[channel]) { this.stop(channel); return; }
    if (!navigator.mediaDevices) throw new Error("Este navegador não oferece captura de mídia.");
    const generation = this.generation;
    const revision = ++this.captureRevision[channel];
    let pipeline: VoicePipeline | undefined;
    if (channel === "voice" && !supplied) { this.voiceAbort?.abort(); this.voiceAbort = new AbortController(); }
    if (channel === "voice" && !supplied) pipeline = await createVoicePipeline(this.state.audioPreferences, audio => {
      if (generation === this.generation && revision === this.captureRevision.voice) {
        if (audio.mode === "off" && this.state.voice) this.stop("voice");
        this.update({ audio });
      }
    }, undefined, this.voiceAbort?.signal);
    const stream = supplied ?? pipeline?.stream ?? (channel === "screen"
      ? await navigator.mediaDevices.getDisplayMedia(displayOptions())
      : await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false }));
    if (generation !== this.generation || revision !== this.captureRevision[channel] || this.state.status !== "online") { pipeline?.close(); stream.getTracks().forEach(track => track.stop()); return; }
    const track = channel === "screen" ? stream.getVideoTracks()[0] : stream.getAudioTracks()[0];
    if (!track || track.readyState === "ended") { pipeline?.close(); stream.getTracks().forEach(track => track.stop()); throw new Error(channel === "screen" ? "O navegador não forneceu uma tela. Escolha a fonte novamente." : "O microfone não forneceu áudio."); }
    if (pipeline) this.voicePipeline = pipeline;
    track?.addEventListener("ended", () => { if (this.state[channel] === stream) this.stop(channel); }, { once: true });
    this.update({ [channel]: stream, error: "" });
    if (!this.send({ type: "publish", channel, active: true })) this.stop(channel);
  }
  stop(channel: Channel) {
    this.captureRevision[channel]++;
    const stream = this.state[channel];
    if (channel === "voice") { this.voiceAbort?.abort(); this.voiceAbort = undefined; this.voicePipeline?.close(); this.voicePipeline = undefined; this.update({ audio: { ...silentAudio } }); }
    this.update({ [channel]: null });
    stream?.getTracks().forEach(track => track.stop());
    this.send({ type: "publish", channel, active: false });
    for (const [id, wire] of this.wires) if (wire.publishing && wire.channel === channel) this.drop(id);
  }
  async configureAudio(value: Partial<AudioPreferences>) {
    const next = normalizeAudio({ ...this.state.audioPreferences, ...value });
    if (this.state.voice && next.deviceId !== this.state.audioPreferences.deviceId) throw new Error("Desligue o microfone antes de trocar o dispositivo.");
    if (this.state.voice && next.denoise !== this.state.audioPreferences.denoise) throw new Error("Desligue o microfone antes de trocar a redução avançada.");
    await this.voicePipeline?.update(next);
    saveAudio(next); this.update({ audioPreferences: next });
  }
  watch = (peerId: string, channel: Channel = "screen") => {
    if (this.state.feeds.some(feed => feed.peerId === peerId && feed.channel === channel)) return;
    const key = `${peerId}:${channel}`;
    if (this.state.waiting.includes(key)) return;
    if (channel === "screen" && this.state.feeds.filter(feed => feed.channel === "screen").length + this.state.waiting.filter(key => key.endsWith(":screen")).length >= this.state.limits.watching) { this.update({ error: `Escolha até ${this.state.limits.watching} telas por vez.` }); return; }
    if (this.send({ type: "subscribe", peerId, channel })) this.update({ waiting: [...this.state.waiting, key] });
  };
  unwatch = (id: string) => { this.send({ type: "unsubscribe", connectionId: id }); this.drop(id); };
  retry = (id: string) => {
    const feed = this.state.feeds.find(feed => feed.id === id);
    if (!feed) return;
    this.unwatch(id);
    this.dismissError();
    this.watch(feed.peerId, feed.channel);
  };
  post = (text: string) => {
    const clientId = crypto.randomUUID();
    return this.send({ type: "post", text, clientId }) ? clientId : null;
  };
  private drop(id: string) {
    const wire = this.wires.get(id);
    if (wire?.timeout) clearTimeout(wire.timeout);
    wire?.pc.close(); this.wires.delete(id);
    this.update({ feeds: this.state.feeds.filter(feed => feed.id !== id) });
  }
  private makeWire(id: string, peerId: string, channel: Channel, publishing: boolean) {
    if (this.wires.has(id)) this.drop(id);
    const pc = new RTCPeerConnection(this.rtc);
    const wire: Wire = { pc, peerId, channel, publishing, candidates: [] };
    this.wires.set(id, wire);
    pc.onicecandidate = event => { if (event.candidate && this.wires.get(id) === wire) this.send({ type: "relay", connectionId: id, data: { candidate: event.candidate.toJSON() } }); };
    pc.ontrack = event => {
      if (this.wires.get(id) !== wire) return;
      const existing = this.state.feeds.find(feed => feed.id === id)?.stream;
      const stream = existing ?? event.streams[0] ?? new MediaStream();
      if (!stream.getTracks().includes(event.track)) stream.addTrack(event.track);
      this.update({ feeds: this.state.feeds.map(feed => feed.id === id ? { ...feed, stream } : feed) });
    };
    pc.onconnectionstatechange = () => {
      if (this.wires.get(id) !== wire || publishing || pc.connectionState === "closed") return;
      if (["connected", "failed"].includes(pc.connectionState) && wire.timeout) { clearTimeout(wire.timeout); wire.timeout = undefined; }
      if (pc.connectionState === "disconnected" && !wire.timeout) wire.timeout = setTimeout(() => {
        if (this.wires.get(id) !== wire) return;
        wire.timeout = undefined;
        if (pc.connectionState === "connected" || pc.connectionState === "closed") return;
        pc.close();
        this.update({ feeds: this.state.feeds.map(feed => feed.id === id ? { ...feed, status: "failed" } : feed), error: "A transmissão não recuperou a conexão. Use Tentar novamente." });
      }, 15000);
      this.update({ feeds: this.state.feeds.map(feed => feed.id === id ? { ...feed, status: pc.connectionState } : feed) });
      if (pc.connectionState === "failed") this.update({ error: this.state.relayAvailable ? "A conexão da transmissão falhou. Tente novamente; se persistir, precisamos conferir a rede e o retransmissor." : "A transmissão não conectou entre estas redes. O retransmissor ainda não está configurado neste servidor." });
    };
    if (!publishing) {
      this.update({ feeds: [...this.state.feeds, { id, peerId, channel, stream: null, status: "new" }], waiting: this.state.waiting.filter(key => key !== `${peerId}:${channel}`) });
      wire.timeout = setTimeout(() => {
        if (this.wires.get(id) !== wire) return;
        wire.timeout = undefined;
        pc.close();
        this.update({ feeds: this.state.feeds.map(feed => feed.id === id ? { ...feed, status: "failed" } : feed), error: "A transmissão demorou para conectar. Você pode tentar novamente." });
      }, 20000);
    }
    return wire;
  }
  private async receive(message: ServerMessage) {
    switch (message.type) {
      case "welcome":
        this.retryCount = 0;
        this.configureConnection(message.rtc ?? { iceServers: [], iceTransportPolicy: "all" });
        this.update({ status: "online", self: message.self, members: message.members, posts: message.posts, limits: message.limits, error: "" });
        this.voiceSubscriptions(); break;
      case "media-config": this.configureConnection(message.rtc); break;
      case "members":
        this.update({ members: message.members, waiting: this.state.waiting.filter(key => message.members.some(member => `${member.peerId}:voice` === key && member.voice || `${member.peerId}:screen` === key && member.screen)) }); this.voiceSubscriptions(); break;
      case "publication":
        this.update({ members: this.state.members.map(member => member.peerId === message.peerId ? { ...member, [message.channel]: message.active } : member) });
        if (message.active && message.channel === "voice" && message.peerId !== this.state.self?.peerId) this.watch(message.peerId, "voice");
        break;
      case "subscription": this.makeWire(message.connectionId, message.peerId, message.channel, false); break;
      case "demand": {
        const stream = this.state[message.channel];
        if (!stream) { this.send({ type: "unsubscribe", connectionId: message.connectionId }); break; }
        const wire = this.makeWire(message.connectionId, message.peerId, message.channel, true);
        const { pc } = wire;
        for (const track of stream.getTracks()) pc.addTrack(track, stream);
        const offer = await pc.createOffer();
        if (this.wires.get(message.connectionId) !== wire) return;
        await pc.setLocalDescription(offer);
        if (this.wires.get(message.connectionId) !== wire) return;
        for (const sender of pc.getSenders()) {
          const parameters = sender.getParameters();
          if (sender.track?.kind === "video" && parameters.encodings.length) {
            parameters.encodings[0].maxBitrate = 1200000;
            parameters.encodings[0].maxFramerate = 24;
            parameters.degradationPreference = "maintain-resolution";
            void sender.setParameters(parameters).catch(() => { /* Browser may not support bitrate control. */ });
          }
        }
        this.send({ type: "relay", connectionId: message.connectionId, data: { description: offer } });
        break;
      }
      case "relay": await this.negotiate(message.connectionId, message.data); break;
      case "connection-closed": this.drop(message.connectionId); break;
      case "post": this.update({ posts: [...this.state.posts.filter(post => post.id !== message.post.id), message.post].slice(-200) }); break;
      case "error":
        if (message.operation === "publish-screen") this.stop("screen");
        this.update({ error: message.message, waiting: message.operation === "subscribe" ? [] : this.state.waiting }); break;
    }
  }
  private voiceSubscriptions() {
    for (const member of this.state.members) if (member.voice && member.peerId !== this.state.self?.peerId) this.watch(member.peerId, "voice");
  }
  private configureConnection(rtc: MediaConnection) {
    this.rtc = rtc;
    this.update({ relayAvailable: rtc.iceServers.some(server => (typeof server.urls === "string" ? [server.urls] : server.urls).some(url => /^turns?:/.test(url))) });
  }
  async diagnostics() {
    let videoFrames = 0, audioBytes = 0;
    const channels = { voice: { videoFrames: 0, audioBytes: 0 }, screen: { videoFrames: 0, audioBytes: 0 } };
    for (const wire of this.wires.values()) {
      if (wire.publishing) continue;
      const stats = await wire.pc.getStats();
      stats.forEach(entry => {
        if (entry.type !== "inbound-rtp") return;
        if (entry.kind === "video") { const frames = Number(entry.framesDecoded ?? 0); videoFrames += frames; channels[wire.channel].videoFrames += frames; }
        if (entry.kind === "audio") { const bytes = Number(entry.bytesReceived ?? 0); audioBytes += bytes; channels[wire.channel].audioBytes += bytes; }
      });
    }
    return { videoFrames, audioBytes, channels };
  }
  private async negotiate(id: string, data: SignalData) {
    const wire = this.wires.get(id);
    if (!wire) return;
    if (data.description) {
      await wire.pc.setRemoteDescription(data.description);
      if (this.wires.get(id) !== wire) return;
      for (const candidate of wire.candidates.splice(0)) await wire.pc.addIceCandidate(candidate);
      if (data.description.type === "offer" && !wire.publishing) {
        const answer = await wire.pc.createAnswer();
        if (this.wires.get(id) !== wire) return;
        await wire.pc.setLocalDescription(answer);
        if (this.wires.get(id) !== wire) return;
        this.send({ type: "relay", connectionId: id, data: { description: answer } });
      }
    } else if (data.candidate) {
      if (wire.pc.remoteDescription) await wire.pc.addIceCandidate(data.candidate);
      else wire.candidates.push(data.candidate);
    }
  }
}
